import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ChatContext = createContext(null);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};

const EMPTY_UNREAD = { room: 0, mention_total: 0, dm_total: 0, dm_by_user: {}, total: 0 };

/**
 * Owns the always-on chat state: unread polling, the open/close of the drawer,
 * and attention notifications (soft ping + flashing tab + desktop notification)
 * whenever a NEW DM arrives or you're @mentioned in the company Room.
 *
 * Lives above both the header trigger button and the ChatRoom drawer so they
 * share one source of truth and we poll unread only once.
 */
export const ChatProvider = ({ children }) => {
  const { token, user, getSelectedLocation } = useAuth();

  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(EMPTY_UNREAD);
  const [flash, setFlash] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem('chat_muted') === '1');

  // @mention "check the message?" popup
  const [pendingAlert, setPendingAlert] = useState(null);
  // Scope the drawer should jump to (consumed by ChatRoom): {type:'room'} | {type:'dm', user:{...}}
  const [targetScope, setTargetScope] = useState(null);

  const prevRef = useRef(null); // {dm_total, mention_total} from previous poll
  const flashTimer = useRef(null);
  const audioCtxRef = useRef(null);
  const openRef = useRef(false);
  const activeScopeRef = useRef(null);   // 'room' | `dm:<id>` | null — reported by ChatRoom
  const pendingAlertRef = useRef(null);
  const ackRef = useRef(new Set(JSON.parse(localStorage.getItem('chat_ack_mentions') || '[]')));
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { pendingAlertRef.current = pendingAlert; }, [pendingAlert]);

  const ackMention = useCallback((id) => {
    if (!id) return;
    ackRef.current.add(id);
    const arr = Array.from(ackRef.current).slice(-300);
    ackRef.current = new Set(arr);
    localStorage.setItem('chat_ack_mentions', JSON.stringify(arr));
  }, []);

  // Keep the current location in a ref so the heartbeat interval stays stable.
  const currentLoc = getSelectedLocation ? getSelectedLocation() : null;
  const locRef = useRef(null);
  locRef.current = currentLoc;

  // ----- soft "ping" via Web Audio (no asset) -----
  const playPing = useCallback(() => {
    if (muted) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = audioCtxRef.current || new AC();
      audioCtxRef.current = ctx;
      const now = ctx.currentTime;
      [880, 1180].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const start = now + i * 0.12;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.14, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.2);
      });
    } catch (e) { /* ignore */ }
  }, [muted]);

  const triggerFlash = useCallback(() => {
    setFlash(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(false), 6000);
  }, []);

  const desktopNotify = useCallback((kind) => {
    try {
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      if (!document.hidden) return; // only when the tab isn't focused
      const title = kind === 'mention' ? '📣 You were mentioned' : '💬 New direct message';
      const body = kind === 'mention' ? 'Someone mentioned you in the Chat Room.' : 'You have a new direct message.';
      const n = new Notification(title, { body, tag: 'rad-chat', renotify: true });
      n.onclick = () => { window.focus(); n.close(); };
    } catch (e) { /* ignore */ }
  }, []);

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/chat/unread`, { headers: { Authorization: `Bearer ${token}` } });
      const data = { ...EMPTY_UNREAD, ...res.data };
      setUnread(data);

      const prev = prevRef.current;
      if (prev) {
        const newDm = data.dm_total > prev.dm_total;
        const newMention = data.mention_total > prev.mention_total;
        if (newDm || newMention) {
          // Visual + desktop notification on any new DM/mention, but the
          // sound ping is reserved for @mentions only (played in fetchAlerts).
          triggerFlash();
          desktopNotify(newMention ? 'mention' : 'dm');
        }
      }
      prevRef.current = { dm_total: data.dm_total, mention_total: data.mention_total };
    } catch (e) { /* silent */ }
  }, [token, triggerFlash, desktopNotify]);

  const scopeKeyOfAlert = (a) => (a.scope === 'room' ? 'room' : `dm:${a.peer_id}`);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/chat/mention-alerts`, { headers: { Authorization: `Bearer ${token}` } });
      const alerts = res.data.alerts || [];
      if (pendingAlertRef.current) return; // one popup at a time
      const candidate = alerts.find((a) => {
        if (ackRef.current.has(a.id)) return false;
        // Don't pop if I'm already looking at that exact conversation.
        if (openRef.current && scopeKeyOfAlert(a) === activeScopeRef.current) return false;
        return true;
      });
      if (candidate) {
        setPendingAlert(candidate);
        playPing();
        triggerFlash();
      }
    } catch (e) { /* silent */ }
  }, [token, playPing, triggerFlash]);

  // Poll unread + mention alerts while logged in
  useEffect(() => {
    if (!token) { prevRef.current = null; setUnread(EMPTY_UNREAD); return; }
    fetchUnread();
    fetchAlerts();
    const id = setInterval(() => { fetchUnread(); fetchAlerts(); }, 5000);
    return () => clearInterval(id);
  }, [token, fetchUnread, fetchAlerts]);

  // Presence heartbeat — marks me online + records my current location
  const heartbeat = useCallback(async () => {
    if (!token) return;
    try {
      const loc = locRef.current;
      await axios.post(`${API}/api/chat/heartbeat`,
        { location_id: loc?.id || null, location_name: loc?.name || null },
        { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    heartbeat();
    const id = setInterval(heartbeat, 30000);
    const onVis = () => { if (!document.hidden) heartbeat(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [token, heartbeat]);

  const openChat = useCallback(() => {
    setOpen(true);
    setFlash(false);
    // Ask for desktop notification permission on first explicit open.
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
      // Unlock audio on the user gesture so future pings can play.
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = audioCtxRef.current || new AC();
        audioCtxRef.current = ctx;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      }
    } catch (e) { /* ignore */ }
  }, []);

  const closeChat = useCallback(() => { setOpen(false); fetchUnread(); }, [fetchUnread]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem('chat_muted', next ? '1' : '0');
      return next;
    });
  }, []);

  // ----- mention popup actions -----
  const checkMention = useCallback(() => {
    const a = pendingAlertRef.current;
    if (!a) return;
    ackMention(a.id);
    setPendingAlert(null);
    if (a.scope === 'dm' && a.peer_id) {
      setTargetScope({ type: 'dm', user: { id: a.peer_id, name: a.peer_name, role: a.peer_role } });
    } else {
      setTargetScope({ type: 'room' });
    }
    openChat();
  }, [ackMention, openChat]);

  const dismissMention = useCallback(() => {
    const a = pendingAlertRef.current;
    if (a) ackMention(a.id);
    setPendingAlert(null);
  }, [ackMention]);

  const reportActiveScope = useCallback((key) => { activeScopeRef.current = key; }, []);
  const consumeTargetScope = useCallback(() => setTargetScope(null), []);

  const value = {
    open, openChat, closeChat,
    unread, refreshUnread: fetchUnread,
    flash, muted, toggleMute,
    currentUserId: user?.id,
    currentLocation: currentLoc?.name || null,
    targetScope, consumeTargetScope, reportActiveScope,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
      {pendingAlert && (
        <MentionAlertPopup alert={pendingAlert} onCheck={checkMention} onDismiss={dismissMention} />
      )}
    </ChatContext.Provider>
  );
};

/** "X has just mentioned you — check the message?" attention popup. */
const MentionAlertPopup = ({ alert, onCheck, onDismiss }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="mention-alert-popup">
    <div className="absolute inset-0 bg-black/50" onClick={onDismiss} />
    <div className="relative w-full max-w-sm bg-slate-900 border-2 border-emerald-500/60 rounded-2xl shadow-2xl p-5 animate-in fade-in zoom-in-95 duration-200">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
          {(alert.sender_name || '?').slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-white text-base leading-snug">
            <span className="font-bold text-emerald-400">{alert.sender_name}</span> has just mentioned you.
          </p>
          <p className="text-gray-400 text-sm mt-0.5">
            {alert.scope === 'dm' ? 'In a direct message.' : 'In the Company Room.'} Check the message now?
          </p>
          {alert.snippet && (
            <p className="text-gray-300 text-xs mt-2 bg-slate-800 rounded-lg px-2.5 py-1.5 line-clamp-2 break-words">“{alert.snippet}”</p>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <button
          onClick={onDismiss}
          data-testid="mention-alert-dismiss"
          className="flex-1 py-2 rounded-lg text-sm font-medium text-gray-300 bg-slate-800 hover:bg-slate-700 transition-colors"
        >
          Dismiss
        </button>
        <button
          onClick={onCheck}
          data-testid="mention-alert-check"
          className="flex-1 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
        >
          Check message
        </button>
      </div>
    </div>
  </div>
);
